// src/rbac/useRbac.ts
// Canonical RBAC hooks for main-app (Phase 3) — mirror of the admin-portal
// selectors, reading the shared generated binding. Drive the Administration
// area's gating off these so main-app and the operator console agree on roles.
import { useAppSelector } from '@/app/hooks';
import { normalizeRoles, tokenScope, hasPermission, scopeForPermission } from './rbac.generated';

const rawRoles = (user: { roles?: string[]; role?: string } | null | undefined): string[] =>
  Array.isArray(user?.roles) && user!.roles!.length
    ? user!.roles!
    : user?.role
      ? [user.role]
      : [];

/** The current user's canonical (alias-normalized) roles. */
export function useRbacRoles(): string[] {
  const user = useAppSelector((s) => s.auth.user);
  return normalizeRoles(rawRoles(user));
}

/** Token-level scope derived from the user's roles. */
export function useScope(): 'platform' | 'tenant' {
  return tokenScope(useRbacRoles());
}

// Server-resolved permission strings (state.auth.permissions is seeded from the
// login/session response, which includes dynamic custom-role grants the static
// role map knows nothing about). Stored as strings in practice; tolerate the
// legacy {resource, action} object shape defensively.
function useExplicitPermissions(): Set<string> {
  const stored = useAppSelector((s) => (s.auth as { permissions?: unknown }).permissions);
  const list = Array.isArray(stored) ? stored : [];
  return new Set(
    list
      .map((p) =>
        typeof p === 'string'
          ? p
          : p && typeof p === 'object' && 'resource' in p && 'action' in p
            ? `${(p as { resource: string }).resource}:${(p as { action: string }).action}`
            : '',
      )
      .filter(Boolean),
  );
}

/** Whether the current user holds `permission` — via the shared RBAC map OR the
 *  server-resolved permission set (which includes dynamic custom-role grants). */
export function useHasPermission(permission: string): boolean {
  const explicit = useExplicitPermissions();
  return (
    hasPermission(useRbacRoles(), permission) || explicit.has(permission) || explicit.has('*')
  );
}

/** Whether the current user holds `permission` TENANT-wide. Some permissions
 *  are granted at narrower scope to some roles (e.g. employee:change:approve:
 *  tenant for HR/owner/finance, TEAM for line managers) and a few surfaces using
 *  them are company-wide (document verification, lifecycle completion). The
 *  role-derived grant scope comes from the shared binding; a holder whose grant
 *  is only in the server-resolved list (custom role, no scope info) keeps the
 *  legacy tenant reach — mirrors employee-service is_tenant_change_approver. */
export function useHasTenantWidePermission(permission: string): boolean {
  const roles = useRbacRoles();
  const explicit = useExplicitPermissions();
  if (explicit.has('*')) return true;
  const roleScope = scopeForPermission(roles, permission);
  if (roleScope) return roleScope === 'tenant';
  return explicit.has(permission);
}

/** Whether the current user holds ANY of `permissions`. */
export function useHasAnyPermission(permissions: string[]): boolean {
  const roles = useRbacRoles();
  const explicit = useExplicitPermissions();
  if (explicit.has('*')) return permissions.length > 0;
  return permissions.some((p) => hasPermission(roles, p) || explicit.has(p));
}

/** A stable predicate `(permission) => boolean` for checking many permissions in
 *  a render (e.g. filtering a catalog) without one hook call per item. */
export function usePermissionChecker(): (permission: string) => boolean {
  const roles = useRbacRoles();
  const explicit = useExplicitPermissions();
  const wildcard = explicit.has('*');
  return (permission: string) =>
    wildcard || explicit.has(permission) || hasPermission(roles, permission);
}
